import { LitElement, html, css } from 'lit-element';

/**
 * Chat text bubble
 *
 * @slot - The text content (the default slot)
 *
 * @csspart icon
 * @csspart text
 *
 * @cssprop [--prev-links-radius=8px 8px 8px 0] - text border radius
 * @cssprop [--prev-links-text-font-size=1rem] - text font size
 * @cssprop [--prev-links-text-bg=#52ca9c] - user text background color
 * @cssprop [--prev-links-text-color=#000] - user text color
 * @cssprop [--prev-links-text-bg-bot=#f3f7f9] - bot text background color
 * @cssprop [--prev-links-text-bg-bot=#000] - bot text color
 */

export class PrevLinks extends LitElement {
  static get properties() {
    return {
      avatar: { type: String },
      avatarAlt: { type: String },
      bot: {
        type: Boolean,
        reflects: true,
      },
      imgUrl: { type: String },
      pageUrl: { type: String },
      title: { type: String },
      description: { type: String },
    };
  }

  constructor() {
    super();
    this.avatarAlt = '';
    this.bot = false;
    this.titleLength = 40;
    this.defaultImg = new URL('../assets/oney_logo.png', import.meta.url).pathname;
  }

  render() {
    let { title, description } = this;
    const { imgUrl, pageUrl } = this;
    const img = imgUrl || this.defaultImg;
    title = title !== '' ? title : pageUrl;
    title = title.length > this.titleLength ? `${title.substring(0, this.titleLength)}...` : title;
    description = description !== '' ? description : 'No description';
    return html`
      <div part="fragment">
        <a href="${pageUrl}" target="_blank">
          <div part="imgpart">
            <img src="${img}" alt="some description" />
          </div>
          <div part="textpart">
            <div part="text-contentpart">
              <h3>${title}</h3>
              <p class="text">
                ${description}
              </p>
            </div>
          </div>
        </a>
      </div>
    `;
  }

  static get styles() {
    return css`
      :host {
        display: flex;
        align-items: flex-end;
        box-sizing: border-box;
        padding: 4px 6px;
        width: 100%;
      }

      [part='icon'] {
        width: 1.5rem;
        height: auto;
        margin: 0 5px;
        background-color: var(--prev-links-icon-background, var(--prev-links-background, #006699));
        border-radius: var(--prev-links-icon-radius, 100%);
      }

      [part='text'] {
        padding: 1rem;
        background-color: var(--prev-links-background, #006699);
        color: var(--prev-links-color, #fff);
        border-radius: var(--prev-links-radius, 8px 8px 8px 0);
        font-size: var(--prev-links-font-size, 1rem);
        word-break: break-word;
      }

      [part='imgpart'] {
        display: inline-block;
        position: relative;
        float: left;
        flex-direction: column;
        width: 30%;
        height: 100%;
        max-width: 100px;
        border-top-left-radius: 8px;
        border-bottom-left-radius: 8px;
        box-sizing: border-box;
        overflow: hidden;
        z-index: 1;
      }

      [part='textpart'] {
        box-sizing: border-box;
        padding: 5px;
        display: inline-block;
        position: relative;
        float: left;
        flex-direction: column;
        height: 100%;
        width: 70%;
      }

      [part='text-contentpart'] {
        box-sizing: border-box;
        height: 100%;
        overflow: hidden;
      }

      :host([bot]) [part='icon'] {
        background-color: var(
          --prev-links-icon-bot-background,
          var(--prev-links-bot-background, #f3f7f9)
        );
      }

      :host([bot]) [part='text'] {
        background-color: var(--prev-links-bot-background, #f3f7f9);
        color: var(--prev-links-bot-color, #000);
      }

      [part='fragment'] {
        font-size: var(--chat-input-font-size, 0.5rem);
        height: 65px;
        border: 0.5px solid #ccc;
        border-radius: 8px;
        width: 80%;
        padding: 0 1.4rem 0 0;
        bottom: 0;
        right: 0;
        left: 0;
        top: 0;
        box-sizing: content-box;
        text-decoration: none;
        position: relative;
        background-color: #f3f7f9;
      }

      [part='fragment']:hover {
        box-shadow: 2px 2px 5px rgba(0, 0, 0, 0.2);
      }

      [part='imgpart'] img {
        max-height: 100%;
        max-width: 100%;
        position: absolute;
        top: 0;
        bottom: 0;
        left: 0;
        right: 0;
        margin: auto;
        overflow: hidden;
      }

      [part='fragment'] h3 {
        padding: 0;
        padding-bottom: 5px;
        margin: 0;
        color: #369;
      }
      .text {
        padding: 0;
        margin-block: inherit;
        color: #7a7a7a;
      }
    `;
  }
}
